import {
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Autocomplete,
  AutocompleteProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';

import { NoResultItem } from '@/components/FormElements';
import { IAddressForm } from '@/models';

import {
  AddressOptionItemRoot,
  getAddressSearchAutocompleteSx,
  OptionListPaper,
  TextField,
} from './styles';

export interface AddressOptionType extends IAddressForm {
  placeId?: string;
  mainText?: string;
  secondaryText?: string;
}

interface AddressSearchProps
  extends Omit<
    AutocompleteProps<AddressOptionType | string, false, boolean, false>,
    'renderInput' | 'onInputChange' | 'renderOption'
  > {
  renderOption: (option: AddressOptionType) => ReactNode;
  overridePlaceClick?: boolean;
  handleInputChange?: (newInputValue: string) => void;
  extraOptionItem?: ReactNode;
  inputRef?: Ref<any>;
  disabled?: boolean;
  autoFocus?: boolean;
  showTruncatedTooltip?: boolean;
}

export const AddressSearch = (props: AddressSearchProps) => {
  const {
    renderOption,
    handleInputChange,
    overridePlaceClick,
    extraOptionItem,
    inputRef,
    disabled,
    autoFocus,
    showTruncatedTooltip,
    sx,
    ...restProps
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [isInputTruncated, setIsInputTruncated] = useState(false);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const [selectedValue, setSelectedValue] = useState<
    AddressOptionType | string | null
  >(restProps.value ?? null);
  const [prevValue, setPrevValue] = useState(restProps.value ?? null);

  // Sync state when parent value changes (synchronous — no render delay)
  const currentValue = restProps.value ?? null;
  if (prevValue !== currentValue) {
    setPrevValue(currentValue);
    if (selectedValue !== currentValue) {
      setSelectedValue(currentValue);
    }
    if (!currentValue) {
      setInputValue('');
    }
  }

  const setCombinedInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputElementRef.current = node;
      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef && 'current' in inputRef) {
        (inputRef as { current: HTMLInputElement | null }).current = node;
      }
    },
    [inputRef],
  );

  const updateTruncationState = useCallback(() => {
    if (!showTruncatedTooltip || !inputElementRef.current) {
      setIsInputTruncated(false);
      return;
    }

    const inputElement = inputElementRef.current;
    const hasInputValue = !!inputElement.value.trim();
    setIsInputTruncated(
      hasInputValue && inputElement.scrollWidth > inputElement.clientWidth,
    );
  }, [showTruncatedTooltip]);

  useEffect(() => {
    updateTruncationState();
  }, [inputValue, selectedValue, updateTruncationState]);

  useEffect(() => {
    if (!showTruncatedTooltip) {
      return;
    }

    const handleResize = () => updateTruncationState();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showTruncatedTooltip, updateTruncationState]);

  const handleSelect = (option: AddressOptionType | string, e: any) => {
    if (restProps.onChange) {
      restProps.onChange(e, option, 'selectOption');
    }

    setSelectedValue(option);
    setOpen(false);
  };

  const renderOptionItem = (
    renderProps: React.HTMLAttributes<HTMLLIElement> & { key: string },
    option: AddressOptionType | string,
  ) => {
    if (!option) return null;
    const { key, ...restRenderProps } = renderProps;
    return (
      <AddressOptionItemRoot
        key={key}
        {...restRenderProps}
        {...(overridePlaceClick
          ? {
              onClick: (e) => {
                e.stopPropagation();
                handleSelect(option, e);
              },
            }
          : {})}
      >
        {typeof option === 'string' ? (
          <Typography variant="subtitle2">{option}</Typography>
        ) : (
          renderOption(option)
        )}
      </AddressOptionItemRoot>
    );
  };

  const tooltipTitle =
    showTruncatedTooltip && isInputTruncated
      ? inputElementRef.current?.value || ''
      : '';

  const defaultAutocompleteSx = getAddressSearchAutocompleteSx(disabled);

  const autocompleteSx = Array.isArray(sx)
    ? [defaultAutocompleteSx, ...sx]
    : sx
    ? [defaultAutocompleteSx, sx]
    : defaultAutocompleteSx;

  return (
    <Autocomplete
      value={selectedValue ?? restProps.value ?? null}
      sx={autocompleteSx}
      disabled={disabled}
      forcePopupIcon={false}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableClearable={!selectedValue && !inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        if (handleInputChange) handleInputChange(newInputValue);
      }}
      clearIcon={
        <ClearIcon
          {...(overridePlaceClick
            ? {
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setSelectedValue(null);
                  if (restProps.onChange) {
                    restProps.onChange(e, '', 'clear');
                  }
                  setOpen(false);
                },
              }
            : {})}
          sx={{ width: 20, height: 20 }}
        />
      }
      noOptionsText={
        <NoResultItem sx={{ display: !inputValue ? 'none' : 'flex' }} />
      }
      renderInput={(params) => (
        <Tooltip
          title={tooltipTitle}
          arrow
          disableFocusListener
          disableTouchListener
          disableHoverListener={!tooltipTitle}
        >
          <TextField
            {...params}
            inputRef={setCombinedInputRef}
            autoFocus={autoFocus}
            autoComplete="off"
            autoCorrect="off"
            placeholder=""
            disabled={disabled}
            onMouseEnter={updateTruncationState}
            sx={{
              '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: '#CFD6DA',
                  boxShadow: '0px 1px 4px rgba(16, 30, 54, 0.06)',
                },
            }}
          />
        </Tooltip>
      )}
      PaperComponent={({ children, ...rest }) => (
        <OptionListPaper {...rest}>
          {children}
          {extraOptionItem}
        </OptionListPaper>
      )}
      renderOption={renderOptionItem}
      clearOnBlur={false}
      disablePortal
      {...restProps}
      inputValue={inputValue}
    />
  );
};
